"use client";

import React, { useState, useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const categories = [
{
  id: 'daily',
  emoji: '🏠',
  title: '日用品',
  titleEn: 'Daily Necessities',
  desc: '覆蓋家庭日常所需的全品類日用消耗品，是信悅最核心的業務板塊。',
  skus: '2,000+',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1c4ca5280-1773033390565.png",
  alt: 'Organized household daily necessities including cleaning products and personal care items',
  subcategories: [
  '清潔用品（洗衣液、洗潔精、消毒液）',
  '個人護理（洗髮水、沐浴露、護膚品）',
  '家居收納（收納盒、掛鉤、整理架）',
  '紙製品（紙巾、濕紙巾、廚房紙）',
  '文具辦公（筆、本子、文件夾）',
  '家用雜貨（電池、燈泡、膠帶）']

},
{
  id: 'baby',
  emoji: '👶',
  title: '母嬰用品',
  titleEn: 'Maternal & Baby',
  desc: '專注0-6歲嬰幼兒及孕期媽媽的全品類產品，安全認證，品質嚴格把控。',
  skus: '800+',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1598e07f6-1765125327744.png",
  alt: 'Safe and colorful baby products including bottles toys and care essentials on pastel background',
  subcategories: [
  '嬰兒餵養（奶瓶、奶嘴、輔食工具）',
  '嬰兒護理（洗護用品、棉柔巾）',
  '孕婦用品（孕婦枕、托腹帶）',
  '嬰兒服裝（連體衣、睡袋）',
  '兒童玩具（益智玩具、積木）',
  '嬰兒出行（推車配件、安全座椅）']

},
{
  id: 'appliances',
  emoji: '🔌',
  title: '小家電',
  titleEn: 'Small Appliances',
  desc: '精選生活品質提升型小家電，覆蓋廚房、個人護理、家居清潔等場景。',
  skus: '500+',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1d95202e4-1773033391080.png",
  alt: 'Modern small home appliances including blender coffee maker and kitchen electronics',
  subcategories: [
  '廚房小電（電飯鍋、豆漿機、榨汁機）',
  '個人護理（電吹風、電動牙刷、剃鬚刀）',
  '家居清潔（掃地機器人、吸塵器）',
  '健康電器（按摩儀、足浴盆）',
  '空氣改善（加濕器、空氣淨化器）',
  '生活小電（充電寶、電風扇）']

},
{
  id: 'kitchen',
  emoji: '🍳',
  title: '廚房用具',
  titleEn: 'Kitchen Tools',
  desc: '從鍋具到刀具，從烘焙到收納，打造完整的廚房解決方案。',
  skus: '1,200+',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_137ef1776-1773033390462.png",
  alt: 'Professional kitchen cookware including pots pans and cooking tools on wooden kitchen counter',
  subcategories: [
  '鍋具（炒鍋、湯鍋、平底鍋）',
  '刀具（廚師刀、水果刀、砧板）',
  '烘焙工具（烤盤、模具、擀麵杖）',
  '廚房收納（調料架、鍋蓋架）',
  '餐具（碗碟、筷子、湯勺）',
  '廚房清潔（洗碗布、清潔劑）']

}];


export default function ProductCategories() {
  const [activeTab, setActiveTab] = useState('daily');
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {if (e.isIntersecting) e.target.classList.remove('reveal-hidden');});
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => {
      el.classList.add('reveal-hidden');
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const active = categories.find((c) => c.id === activeTab)!;

  return (
    <section ref={sectionRef} className="py-24 px-6" style={{ background: '#F5EFE6' }}>
      <div className="max-w-container mx-auto">
        <div className="text-center mb-12 reveal">
          <p className="text-xs font-semibold tracking-widest uppercase text-accent mb-2">
            PRODUCT LINES · 產品線
          </p>
          <h2 className="font-heading font-bold text-brand-fg text-3xl md:text-4xl">
            四大核心品類詳情
          </h2>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-10 reveal">
          {categories.map((cat) =>
          <button
            key={cat.id}
            onClick={() => setActiveTab(cat.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border transition-all duration-200 ${
            activeTab === cat.id ?
            'bg-primary text-white border-primary shadow-md' :
            'bg-white text-brand-muted border-brand-border hover:border-primary/40 hover:text-primary'}`
            }>
            
              <span>{cat.emoji}</span>
              {cat.title}
            </button>
          )}
        </div>

        {/* Active category detail */}
        <div className="reveal grid lg:grid-cols-2 gap-10 bg-white rounded-3xl border border-brand-border overflow-hidden shadow-card">
          <div className="relative h-64 lg:h-auto min-h-[300px]">
            <AppImage
              src={active.img}
              alt={active.alt}
              fill
              className="object-cover" />
            
            <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span
                className="px-4 py-2 rounded-full text-sm font-bold text-white"
                style={{ background: 'rgba(139,94,60,0.85)', backdropFilter: 'blur(8px)' }}>
                
                {active.skus} SKU
              </span>
            </div>
          </div>

          <div className="p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-4xl">{active.emoji}</span>
              <div>
                <h3 className="font-heading font-bold text-brand-fg text-2xl">{active.title}</h3>
                <p className="text-accent text-sm">{active.titleEn}</p>
              </div>
            </div>
            <p className="text-brand-muted leading-relaxed mb-6">{active.desc}</p>

            <div>
              <p className="font-semibold text-brand-fg text-sm mb-3">主要子品類：</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {active.subcategories.map((sub) =>
                <div key={sub} className="flex items-start gap-2">
                    <span className="text-accent mt-1 text-xs">●</span>
                    <p className="text-sm text-brand-muted">{sub}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}